<template>
  <v-app :class="{'homepage-active': isHomePage}">
    <v-app-bar app :color="isDark ? '#2A2A2A' : 'primary'" :dark="isDark">
      <v-toolbar-title>
        {{ websiteName }}
        <v-btn text @click="goToHome">Home</v-btn>
        <v-btn v-if="isAuthenticated" text @click="goToProjects">Projects</v-btn>
        <v-btn text @click="goToAbout">About</v-btn>
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-icon class="mr-2">mdi-white-balance-sunny</v-icon>
      <v-switch class="switch-center" v-model="isDark" inset></v-switch>
      <v-icon class="mr-2">mdi-weather-night</v-icon>
      <v-menu>
        <template v-slot:activator="{ props: menu }">
          <v-tooltip location="top">
            <template v-slot:activator="{ props: tooltip }">
              <v-btn color="secondary" v-bind="mergeProps(menu, tooltip)" icon>
                <v-icon>mdi-account-circle</v-icon>
              </v-btn>
            </template>
            <span>User Options</span>
          </v-tooltip>
        </template>
        <v-list>
          <v-list-item v-if="isAuthenticated" @click="goToUserProfile">
            <v-list-item-title>Profile</v-list-item-title>
          </v-list-item>
          <v-list-item v-if="isAuthenticated" @click="openPrefs">
            <v-list-item-title>Settings</v-list-item-title>
          </v-list-item>
          <v-list-item v-if="isAdmin" @click="goToAdminDashboard">
            <v-list-item-title>Admin Dashboard</v-list-item-title>
          </v-list-item>
          <v-list-item v-if="isAuthenticated" @click="handleLogout">
            <v-list-item-title>Logout</v-list-item-title>
          </v-list-item>
          <v-list-item v-else @click="goToLogin">
            <v-list-item-title>Login</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-app-bar>

    <v-main>
      <v-container class="custom-container">
        <router-view />
      </v-container>
    </v-main>

    <v-footer app :color="isDark ? '#2A2A2A' : 'primary'" :dark="isDark">
      <span class="white--text">{{ websiteName }} v{{ appVersion }}</span>
    </v-footer>
    <!-- Preferences Dialog -->
<v-dialog
  v-model="prefs.open"
  max-width="520"
  scrim="rgba(0,0,0,0.65)"
  transition="dialog-bottom-transition"
>
  <v-card class="rounded-2xl">
    <v-card-title class="py-3 d-flex align-center">
      <v-icon class="mr-2">mdi-cog</v-icon>
      <span class="text-h6">Settings</span>
      <v-spacer />
      <v-btn icon variant="text" @click="closePrefs">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>

    <v-divider />

    <v-card-text class="pt-4">
      <v-list lines="two" density="comfortable">
        <v-list-item>
          <template #prepend>
            <v-icon>mdi-content-save-cog</v-icon>
          </template>
          <v-list-item-title>Automatic saving</v-list-item-title>
          <v-list-item-subtitle>Save changes as you work.</v-list-item-subtitle>
          <template #append>
            <v-switch
              v-model="prefs.autoSave"
              inset
              color="primary"
              hide-details
            />
          </template>
        </v-list-item>

        <v-divider class="my-2" />

        <v-list-item>
          <template #prepend>
            <v-icon>mdi-key-variant</v-icon>
          </template>
          <v-list-item-title>eScriptorium API Token</v-list-item-title>
          <v-list-item-subtitle>For importing and syncing with eScriptorium.</v-list-item-subtitle>
        </v-list-item>
        <v-list-item>
          <v-text-field
            v-model="prefs.escriptoriumToken"
            label="API Token"
            :type="showEscriptoriumToken ? 'text' : 'password'"
            :append-inner-icon="showEscriptoriumToken ? 'mdi-eye-off' : 'mdi-eye'"
            @click:append-inner="showEscriptoriumToken = !showEscriptoriumToken"
            density="compact"
            variant="outlined"
            hide-details
            class="mt-2"
          />
        </v-list-item>
      </v-list>

      <v-alert
        v-if="prefs.autoSave"
        type="info"
        variant="tonal"
        density="comfortable"
        class="mt-3"
      >
        Changes will be saved automatically.
      </v-alert>
    </v-card-text>

    <v-card-actions class="px-4 pb-4">
      <v-btn variant="text" @click="restoreDefaults">
        Restore defaults
      </v-btn>
      <v-spacer />
      <v-btn color="primary" @click="closePrefs">
        Done
      </v-btn>
    </v-card-actions>
  </v-card>
</v-dialog>

    <v-snackbar v-model="snackbar.show" :timeout="snackbar.timeout" top>
      {{ snackbar.message }}
      <v-btn color="red" text @click="snackbar.show = false">Close</v-btn>
    </v-snackbar>
  </v-app>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { mergeProps } from "vue";
import { useDark } from "@vueuse/core";

export default {
  name: "App",
  setup() {
    const isDark = useDark({ initialValue: 'light' });
    return { isDark };
  },
  data() {
    return {
      websiteName: process.env.VUE_APP_WEBSITE_NAME,
      appVersion: (process.env.VUE_APP_APP_VERSION || "dev").replace(/^v/, ""),
      snackbar: {
        show: false,
        message: "",
        timeout: 2000,
      },
        prefs: {
        open: false,
        autoSave: true,
        escriptoriumToken: "",
      },
      showEscriptoriumToken: false,
    };
  },
  computed: {
    ...mapState(["isAuthenticated", "roles"]),
    isAdmin() {
      return this.roles.includes("administrator"); // check if admin
    },
    isHomePage() {
      return this.$route.name === "HomePage";
    },
  },
  methods: {
    ...mapActions(["logout", "fetchUserRoles"]),
    mergeProps,
    goToLogin() {
      this.$router.push("/login");
    },
    goToHome() {
      this.$router.push("/");
    },
    goToProjects() {
      this.$router.push("/projects");
    },
    goToAbout() {
      this.$router.push("/about");
    },
    goToAdminDashboard() {
      this.$router.push("/administrator");
    },
    goToUserProfile() {
      this.$router.push("/profile");
    },
    handleLogout() {
      this.logout();
      this.$router.push("/");
      this.showSnackbar("You have successfully logged out.");
    },
    showSnackbar(message) {
      this.snackbar.message = message;
      this.snackbar.show = true;
    },
        openPrefs() {
      this.loadPrefs();
      this.prefs.open = true;
    },
    closePrefs() {
      this.prefs.open = false;
    },
    loadPrefs() {
      const saved = localStorage.getItem("prefs.autoSave");
      if (saved !== null) this.prefs.autoSave = JSON.parse(saved);

      const savedToken = localStorage.getItem("prefs.escriptoriumToken");
      if (savedToken !== null) this.prefs.escriptoriumToken = savedToken;
    },
    restoreDefaults() {
      this.prefs.autoSave = true;
      this.prefs.escriptoriumToken = "";
    },
  },
  watch: {
    isAuthenticated(newVal) {
      if (newVal) {
        this.fetchUserRoles(); // after login -> fetch user roles
      }
    },
    isDark(newValue) {
      this.$vuetify.theme.global.name = newValue ? 'DarkTheme' : 'CustomTheme';
    },
        "prefs.autoSave"(val) {
      localStorage.setItem("prefs.autoSave", JSON.stringify(val));
      this.showSnackbar(`Automatic saving ${val ? "enabled" : "disabled"}.`);
    },
    "prefs.escriptoriumToken"(val) {
      if (val) {
        localStorage.setItem("prefs.escriptoriumToken", val);
      } else {
        localStorage.removeItem("prefs.escriptoriumToken");
      }
    },
  },
    mounted() {
    // Initialize prefs on app load
    this.loadPrefs();
    this.$vuetify.theme.global.name = this.isDark ? 'DarkTheme' : 'CustomTheme';
  },
};
</script>

<style scoped>
.custom-container {
  margin: 10px auto 0;
  padding: 0;
  max-width: 100%;
  width: calc(100% - 10px);
}
.switch-center {
  display: flex;
  justify-content: center;
}
</style>
